import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import CustomerHeader from "../components/CustomerHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Briefcase, FileText, Upload, Trash2, MapPin, Clock, CheckCircle2, XCircle, Car, IdCard, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CONTRACT_LABEL = { stagionale: "Stagionale", part_time: "Part-time", full_time: "Full-time", altro: "Altro" };
const APP_STATUS = {
  submitted: { l: "Inviata", c: "bg-blue-500/20 text-blue-300" },
  viewed: { l: "Vista", c: "bg-zinc-500/20 text-zinc-300" },
  shortlisted: { l: "In valutazione", c: "bg-amber-500/20 text-amber-300" },
  contacted: { l: "Contattato", c: "bg-purple-500/20 text-purple-300" },
  rejected: { l: "Non selezionato", c: "bg-red-500/20 text-red-300" },
  hired: { l: "Assunto 🎉", c: "bg-[#00E676]/20 text-[#00E676]" },
  withdrawn: { l: "Ritirata", c: "bg-zinc-600/20 text-zinc-400" },
};

export default function Work() {
  const [tab, setTab] = useState("offerte");
  const [roleTypes, setRoleTypes] = useState([]);
  useEffect(() => { api.get("/jobs/meta").then(({ data }) => setRoleTypes(data.role_types)); }, []);
  const roleLabel = (k) => roleTypes.find((r) => r.key === k)?.label || k;

  return (
    <div className="min-h-screen bg-[#0A0A0C] text-white pb-16">
      <CustomerHeader />
      <div className="max-w-4xl mx-auto px-4 lg:px-8 py-6" data-testid="work-page">
        <div className="flex items-center gap-3 mb-5">
          <div className="h-10 w-10 rounded-xl bg-[#00E676]/15 border border-[#00E676]/30 flex items-center justify-center"><Briefcase className="h-5 w-5 text-[#00E676]" /></div>
          <div><h1 className="font-display text-2xl font-bold">Lavora nel Canavese</h1><p className="text-sm text-zinc-400">Trova lavoro nelle attività locali di Fuma c'anduma.</p></div>
        </div>
        <div className="flex gap-2 mb-6 border-b border-white/10">
          {[["offerte", "Offerte"], ["profilo", "Il mio profilo"], ["candidature", "Le mie candidature"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`work-tab-${k}`}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-[#00E676] text-[#00E676]" : "border-transparent text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
        {tab === "offerte" && <Offerte roleTypes={roleTypes} roleLabel={roleLabel} />}
        {tab === "profilo" && <Profilo roleTypes={roleTypes} />}
        {tab === "candidature" && <Candidature roleLabel={roleLabel} />}
      </div>
    </div>
  );
}

function Offerte({ roleTypes, roleLabel }) {
  const [jobs, setJobs] = useState([]);
  const [filter, setFilter] = useState("");
  const [active, setActive] = useState(null);
  const load = (rt) => api.get("/work/jobs", { params: rt ? { role_type: rt } : {} }).then(({ data }) => setJobs(data));
  useEffect(() => { load(filter); }, [filter]);
  return (
    <div>
      <div className="flex gap-2 flex-wrap mb-4">
        <button onClick={() => setFilter("")} className={`text-xs px-3 py-1.5 rounded-full border ${!filter ? "border-[#00E676] text-[#00E676]" : "border-white/10 text-zinc-300"}`} data-testid="work-filter-all">Tutte</button>
        {roleTypes.map((r) => <button key={r.key} onClick={() => setFilter(r.key)} className={`text-xs px-3 py-1.5 rounded-full border ${filter === r.key ? "border-[#00E676] text-[#00E676]" : "border-white/10 text-zinc-300"}`} data-testid={`work-filter-${r.key}`}>{r.label}</button>)}
      </div>
      {jobs.length === 0 && <div className="text-sm text-zinc-500 text-center py-10">Nessuna offerta al momento.</div>}
      <div className="grid gap-3">
        {jobs.map((j) => (
          <div key={j.id} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4" data-testid={`work-job-${j.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="font-semibold">{j.title}</div>
                <div className="text-sm text-zinc-400">{j.business_name} · <MapPin className="inline h-3 w-3" /> {j.zone}</div>
              </div>
              <Badge className="bg-[#00E676]/15 text-[#00E676] border border-[#00E676]/30">{roleLabel(j.role_type)}</Badge>
            </div>
            <div className="flex gap-2 mt-2 text-xs text-zinc-400 flex-wrap">
              <span className="px-2 py-0.5 rounded bg-white/5">{CONTRACT_LABEL[j.contract_type]}</span>
              {j.hours && <span className="px-2 py-0.5 rounded bg-white/5"><Clock className="inline h-3 w-3" /> {j.hours}</span>}
              <span className="px-2 py-0.5 rounded bg-white/5">{Math.max(0, j.positions - j.positions_filled)} posti</span>
              {j.salary_visible && j.salary && <span className="px-2 py-0.5 rounded bg-white/5">€ {j.salary}</span>}
            </div>
            <Button size="sm" className="mt-3 bg-[#00E676] text-black" onClick={() => setActive(j)} data-testid={`work-open-${j.id}`}>Dettagli e candidatura</Button>
          </div>
        ))}
      </div>
      {active && <ApplyModal job={active} roleLabel={roleLabel} onClose={() => setActive(null)} onApplied={() => { setActive(null); toast.success("Candidatura inviata!"); }} />}
    </div>
  );
}

function ApplyModal({ job, roleLabel, onClose, onApplied }) {
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/work/jobs/${job.id}`).then(({ data }) => setDetail(data)); }, [job.id]);
  const apply = async () => {
    setBusy(true);
    try { await api.post(`/work/jobs/${job.id}/apply`, { cover_note: note }); onApplied(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-[#141417] border border-white/10 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="work-apply-modal">
        <div className="font-display text-lg font-bold">{job.title}</div>
        <div className="text-sm text-zinc-400 mb-3">{job.business_name} · {roleLabel(job.role_type)} · {job.zone}</div>
        {detail?.description && <p className="text-sm text-zinc-300 whitespace-pre-wrap mb-3">{detail.description}</p>}
        {detail?.requirements?.length > 0 && <div className="mb-3"><div className="text-xs text-zinc-500 uppercase mb-1">Requisiti</div><ul className="text-sm text-zinc-300 list-disc pl-4">{detail.requirements.map((r, i) => <li key={i}>{r}</li>)}</ul></div>}
        <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Messaggio per l'attività (facoltativo)" className="bg-[#0A0A0C] border-white/10 mb-3" data-testid="work-cover-note" />
        <div className="flex gap-2">
          <Button variant="secondary" onClick={onClose} className="flex-1">Chiudi</Button>
          {detail?.already_applied
            ? <Button disabled className="flex-1">Già candidato</Button>
            : <Button onClick={apply} disabled={busy} className="flex-1 bg-[#00E676] text-black" data-testid="work-apply-submit">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1" /> Candidati</>}</Button>}
        </div>
      </div>
    </div>
  );
}

function Profilo({ roleTypes }) {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);
  const [cvBusy, setCvBusy] = useState(false);
  useEffect(() => { api.get("/work/profile").then(({ data }) => setP(data.exists ? data : { skills: [], desired_roles: [], desired_contract: [], availability: { immediate: false }, max_distance_km: 15, experiences: [], has_license: false, has_vehicle: false })); }, []);
  if (!p) return <div className="text-zinc-400">Caricamento…</div>;
  const set = (k, v) => setP((x) => ({ ...x, [k]: v }));
  const toggleArr = (k, val) => setP((x) => ({ ...x, [k]: (x[k] || []).includes(val) ? x[k].filter((y) => y !== val) : [...(x[k] || []), val] }));
  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...p, skills: typeof p.skills === "string" ? p.skills.split(",").map((s) => s.trim()).filter(Boolean) : p.skills };
      const { data } = await api.put("/work/profile", payload);
      setP(data); toast.success("Profilo salvato");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };
  const uploadCv = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setCvBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/work/profile/cv", fd, { headers: { "Content-Type": "multipart/form-data" } }); setP((x) => ({ ...x, has_cv: true, cv_filename: data.filename })); toast.success("CV caricato"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } finally { setCvBusy(false); }
  };
  const deleteCv = async () => { await api.delete("/work/profile/cv"); setP((x) => ({ ...x, has_cv: false, cv_filename: null })); toast.success("CV eliminato"); };
  const skillsStr = Array.isArray(p.skills) ? p.skills.join(", ") : (p.skills || "");
  return (
    <div className="space-y-5" data-testid="work-profile">
      {/* CV */}
      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4">
        <div className="font-semibold mb-2 flex items-center gap-2"><FileText className="h-4 w-4 text-[#00E676]" /> Curriculum (PDF)</div>
        {p.has_cv ? (
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm text-zinc-300 flex items-center gap-1"><CheckCircle2 className="h-4 w-4 text-[#00E676]" /> {p.cv_filename || "CV caricato"}</span>
            <div className="flex gap-2">
              <label className="text-xs px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 cursor-pointer" data-testid="work-cv-replace">Sostituisci<input type="file" accept="application/pdf" className="hidden" onChange={uploadCv} /></label>
              <Button size="sm" variant="destructive" onClick={deleteCv} data-testid="work-cv-delete"><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ) : (
          <label className="inline-flex items-center gap-2 text-sm px-4 py-2 rounded-lg bg-[#00E676] text-black font-medium cursor-pointer" data-testid="work-cv-upload">
            {cvBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Carica CV<input type="file" accept="application/pdf" className="hidden" onChange={uploadCv} />
          </label>
        )}
        <p className="text-xs text-zinc-500 mt-2">Il tuo CV è privato: visibile solo alle attività a cui ti candidi.</p>
      </div>
      {/* Dati */}
      <div className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4 space-y-3">
        <div><label className="text-sm text-zinc-400">Titolo/presentazione</label><Input value={p.headline || ""} onChange={(e) => set("headline", e.target.value)} placeholder="Es. Pizzaiolo con 3 anni di esperienza" className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="work-headline" /></div>
        <div><label className="text-sm text-zinc-400">Competenze (separate da virgola)</label><Input value={skillsStr} onChange={(e) => set("skills", e.target.value)} placeholder="pizzeria, impasto, forno" className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="work-skills" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-sm text-zinc-400">Indirizzo</label><Input value={p.address || ""} onChange={(e) => set("address", e.target.value)} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="work-address" /></div>
          <div><label className="text-sm text-zinc-400">Distanza max (km)</label><Input type="number" value={p.max_distance_km} onChange={(e) => set("max_distance_km", parseInt(e.target.value || 0))} className="bg-[#0A0A0C] border-white/10 mt-1" data-testid="work-maxdist" /></div>
        </div>
        <div>
          <label className="text-sm text-zinc-400">Ruoli desiderati</label>
          <div className="flex flex-wrap gap-2 mt-1">{roleTypes.map((r) => <button key={r.key} onClick={() => toggleArr("desired_roles", r.key)} className={`text-xs px-3 py-1.5 rounded-full border ${(p.desired_roles || []).includes(r.key) ? "border-[#00E676] text-[#00E676] bg-[#00E676]/10" : "border-white/10 text-zinc-300"}`} data-testid={`work-role-${r.key}`}>{r.label}</button>)}</div>
        </div>
        <div>
          <label className="text-sm text-zinc-400">Contratto desiderato</label>
          <div className="flex flex-wrap gap-2 mt-1">{Object.entries(CONTRACT_LABEL).map(([k, l]) => <button key={k} onClick={() => toggleArr("desired_contract", k)} className={`text-xs px-3 py-1.5 rounded-full border ${(p.desired_contract || []).includes(k) ? "border-[#00E676] text-[#00E676] bg-[#00E676]/10" : "border-white/10 text-zinc-300"}`} data-testid={`work-contract-${k}`}>{l}</button>)}</div>
        </div>
        <div className="flex flex-wrap gap-4 pt-1">
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="work-immediate"><input type="checkbox" checked={!!(p.availability?.immediate)} onChange={(e) => set("availability", { ...(p.availability || {}), immediate: e.target.checked })} /> <Clock className="h-4 w-4" /> Disponibilità immediata</label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="work-license"><input type="checkbox" checked={!!p.has_license} onChange={(e) => set("has_license", e.target.checked)} /> <IdCard className="h-4 w-4" /> Patente</label>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="work-vehicle"><input type="checkbox" checked={!!p.has_vehicle} onChange={(e) => set("has_vehicle", e.target.checked)} /> <Car className="h-4 w-4" /> Automunito</label>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#00E676] text-black w-full" data-testid="work-save-profile">{saving ? "Salvataggio…" : "Salva profilo"}</Button>
      </div>
    </div>
  );
}

function Candidature({ roleLabel }) {
  const [apps, setApps] = useState([]);
  const load = () => api.get("/work/applications").then(({ data }) => setApps(data));
  useEffect(() => { load(); }, []);
  const withdraw = async (id) => { await api.delete(`/work/applications/${id}`); load(); toast.success("Candidatura ritirata"); };
  if (apps.length === 0) return <div className="text-sm text-zinc-500 text-center py-10">Non ti sei ancora candidato a nessuna offerta.</div>;
  return (
    <div className="grid gap-3" data-testid="work-applications">
      {apps.map((a) => {
        const st = APP_STATUS[a.status] || { l: a.status, c: "bg-zinc-500/20" };
        return (
          <div key={a.id} className="bg-[#1C1C1F] border border-white/10 rounded-xl p-4" data-testid={`work-app-${a.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div><div className="font-semibold">{a.job_title}</div><div className="text-sm text-zinc-400">{a.business_name}</div></div>
              <Badge className={`border-0 ${st.c}`}>{st.l}</Badge>
            </div>
            <div className="mt-2 text-sm text-zinc-400">Compatibilità: <b className="text-[#00E676]">{a.match_score}%</b></div>
            {a.status !== "withdrawn" && a.status !== "hired" && <Button size="sm" variant="ghost" className="mt-2 text-red-400" onClick={() => withdraw(a.id)} data-testid={`work-withdraw-${a.id}`}>Ritira candidatura</Button>}
          </div>
        );
      })}
    </div>
  );
}
